import type { CmmdDeviceRegistryDevice, CmmdDeviceRegistryRuntime } from "@cmmd-forge/contracts";
import {
  EXECUTION_TARGET_ROW_CLASSNAME,
  ExecutionTargetActions,
  ExecutionTargetIdentity,
  ITEM_ROW_INNER_CLASSNAME,
} from "./executionTargetRow.shared";
import { presentConnectionLifecycle } from "./connectionLifecyclePresentation";

function isExecutableRegistryRuntime(runtime: CmmdDeviceRegistryRuntime): boolean {
  return runtime.source === "registered" && runtime.runtimeKind === "desktop-local";
}

function registryRuntimeDisplayKey(runtime: CmmdDeviceRegistryRuntime): string {
  if (typeof runtime.hostId === "number") return `host:${runtime.hostId}`;
  if (runtime.deviceId !== null) return `device:${runtime.deviceId}`;
  return `runtime:${runtime.id}`;
}

export function dedupeExecutableRegistryRuntimes(
  runtimes: ReadonlyArray<CmmdDeviceRegistryRuntime>,
): ReadonlyArray<CmmdDeviceRegistryRuntime> {
  const seen = new Set<string>();
  return runtimes.filter((runtime) => {
    if (!isExecutableRegistryRuntime(runtime)) return false;
    const key = registryRuntimeDisplayKey(runtime);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isLinkedExecutableDevice(device: CmmdDeviceRegistryDevice): boolean {
  const metadata = device.metadata;
  const clientKind =
    typeof metadata === "object" && metadata !== null && "clientKind" in metadata
      ? metadata.clientKind
      : null;
  return (
    (device.deviceType === "desktop" || device.deviceType === "server") &&
    clientKind === "forge-desktop" &&
    device.trustState === "trusted" &&
    device.authorization.bound &&
    !device.authorization.revoked
  );
}

export function getUnavailableLinkedDevices(input: {
  readonly devices: ReadonlyArray<CmmdDeviceRegistryDevice>;
  readonly executableRuntimes: ReadonlyArray<CmmdDeviceRegistryRuntime>;
}): ReadonlyArray<CmmdDeviceRegistryDevice> {
  const runtimeDeviceIds = new Set(
    input.executableRuntimes
      .map((runtime) => runtime.deviceId)
      .filter((deviceId): deviceId is number => deviceId !== null),
  );
  return input.devices.filter(
    (device) => isLinkedExecutableDevice(device) && !runtimeDeviceIds.has(device.id),
  );
}

export function LinkedRuntimeExecutionRow({
  runtime,
}: {
  readonly runtime: CmmdDeviceRegistryRuntime;
}) {
  const lifecycle = presentConnectionLifecycle({ kind: "remote-runtime", runtime });
  return (
    <div className={EXECUTION_TARGET_ROW_CLASSNAME} data-slot="execution-target-row">
      <div className={ITEM_ROW_INNER_CLASSNAME} data-slot="execution-target-row-grid">
        <ExecutionTargetIdentity
          tone={lifecycle.tone}
          name={runtime.displayName}
          statusLabel={lifecycle.label}
          accessibleStatus={`Device ${lifecycle.label.toLowerCase()}`}
          // The third column this replaces printed the literal word "CMMD" on
          // every healthy device row: a constant, in the widest column on the
          // page, saying only what the section heading already said.
          meta={["Command device"]}
          note={lifecycle.detail}
        />
        {/* No action here yet — a device is turned on from the machine itself,
            so this page can only say what the machine is doing. */}
        <ExecutionTargetActions
          hint={
            lifecycle.phase === "recovering"
              ? "Checking connection to that machine."
              : lifecycle.phase === "online"
                ? null
                : "Open Forge on that machine to bring this environment back."
          }
        />
      </div>
    </div>
  );
}

export function UnavailableLinkedDeviceRow({
  device,
}: {
  readonly device: CmmdDeviceRegistryDevice;
}) {
  return (
    <div className={EXECUTION_TARGET_ROW_CLASSNAME} data-slot="execution-target-row">
      <div className={ITEM_ROW_INNER_CLASSNAME} data-slot="execution-target-row-grid">
        <ExecutionTargetIdentity
          tone="muted"
          name={device.displayName}
          statusLabel="Unavailable"
          accessibleStatus="Device unavailable"
          meta={["Command device", device.platform]}
          // "Not published" and the old third column's "Not reachable" were
          // two spellings of one fact. Say it once, and say what it means.
          note="Not published, so nothing can run here"
        />
        <ExecutionTargetActions hint="Publish this machine from its own Forge desktop app to run work on it." />
      </div>
    </div>
  );
}
